<script lang="tsx" setup>
import { useI18n } from 'vue-i18n'
import MultiSelectFilter from '../filter/MultiSelectFilter.vue'
import TextFilter from '../filter/TextFilter.vue'
import ResponsibleFilter from '../filter/ResponsibleFilter.vue'
import {
	ConditionallyImplemented,
	Implemented,
	NeedsAttention,
	NotImplemented,
	PartiallyImplemented,
	SMF_Comment,
	SMF_NoComment,
	SMF_NoRuleComment,
	SMF_RuleComment,
	NoKind,
	Ok,
	Remark,
	Defect,
	WillNotApply,
	WillNotImplement,
} from '@/models/enums-generated'
import ModuleFilter from '../filter/ModuleFilter.vue'
import AssetFilter from '../filter/AssetFilter.vue'
import useMethodColumns from './columns'
import { selectItem, TextField, ValueField } from '@/models/select-item'
import FilterContainer from '../filter/FilterContainer.vue'
import SingleSelectFilter from '../filter/SingleSelectFilter.vue'
import InstanceFilter from '../filter/InstanceFilter.vue'
import { onBeforeMount, ref } from 'vue'
import { getMethodStatusText, getAuditText, getPriorityText, getTypeText } from '@/services/utils'
import CalendarFilter from '../filter/CalendarFilter.vue'
import http from '@/services/http'
import type { Response, Responsible } from '@/models/api-generated'

const props = defineProps({
	hideButton: {
		type: Boolean,
		default: false,
	},
	showInstance: {
		type: Boolean,
		default: false,
	},
})
const i18n = useI18n()
const { columns, custom, all } = useMethodColumns()
const versionOptions = ref<{ text: string; value: string }[]>([])

const isKeyOfColumns = (key: string): key is keyof typeof columns => key in columns

const setFilterFromParams = () => {
	const params = new URLSearchParams(window.location.search)
	for (const [key, value] of params) {
		// Key doesn't exist in columns or not a filter param.
		if (!isKeyOfColumns(key)) {
			continue
		}
		const column = columns[key].value
		if (key === 'reference' || key === 'auditName') {
			column.filter = value
		} else if (key === 'dueDate' || key === 'updatedAt') {
			column.filter.push(value)
		} else if (key === 'responsible') {
			const parsed = JSON.parse(value)
			column.filter.push({ id: parsed.id, type: parsed.type })
		} else if (key === 'serviceProvider') {
			const parsed = JSON.parse(value)
			column.filter = parsed.map((p: Responsible) => ({ id: p.id, type: p.type }))
		} else {
			const parsedNumber = parseInt(value)
			if (!isNaN(parsedNumber)) {
				column.filter.push(parsedNumber)
			} else {
				column.filter.push(value)
			}
		}
	}
}

const getVersions = async () => {
	const { data } = await http.get<Response<string[]>>('/api/versions')
	if (data.data) {
		versionOptions.value = data.data.map((v) => selectItem(v, v))
	}
}
onBeforeMount(() => {
	setFilterFromParams()
	getVersions()
})
</script>

<template>
	<FilterContainer :columns="all" :custom="custom" :hide-button="props.hideButton" bookmark="methods-bookmark-">
		<div class="p-2 filter-col" v-if="columns.version.value.isVisible">
			<SingleSelectFilter
				id="version-filter-methods"
				v-model="columns.version.value"
				:label-field="TextField"
				:value-field="ValueField"
				:options="versionOptions"
			></SingleSelectFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.module.value.isVisible">
			<ModuleFilter id="module-filter-methods" v-model="columns.module.value"></ModuleFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.reference.value.isVisible">
			<TextFilter id="reference-filter-methods" v-model="columns.reference.value"></TextFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.desc.value.isVisible">
			<TextFilter id="description-filter-methods" v-model="columns.desc.value"></TextFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.asset.value.isVisible">
			<AssetFilter id="asset-filter-methods" v-model="columns.asset.value"></AssetFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.type.value.isVisible">
			<MultiSelectFilter
				id="type-filter-methods"
				v-model="columns.type.value"
				:label-field="TextField"
				:value-field="ValueField"
				:formatter="getTypeText"
				:options="[
					{ text: i18n.t('main'), value: 'main' },
					{ text: i18n.t('standard'), value: 'standard' },
					{ text: i18n.t('high'), value: 'high' },
				]"
			></MultiSelectFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.responsible.value.isVisible">
			<ResponsibleFilter id="responsible-filter-methods" v-model="columns.responsible.value"></ResponsibleFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.serviceProvider.value.isVisible">
			<ResponsibleFilter id="service-provider-filter-methods" v-model="columns.serviceProvider.value"></ResponsibleFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.priority.value.isVisible">
			<MultiSelectFilter
				id="priority-filter-methods"
				v-model="columns.priority.value"
				:label-field="TextField"
				:value-field="ValueField"
				:formatter="getPriorityText"
				:options="[
					{ text: i18n.t('misc.nothing'), value: undefined },
					{ text: i18n.t('low'), value: 'low' },
					{ text: i18n.t('medium'), value: 'medium' },
					{ text: i18n.t('high'), value: 'high' },
				]"
			></MultiSelectFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.status.value.isVisible">
			<MultiSelectFilter
				id="status-filter-methods"
				v-model="columns.status.value"
				:label-field="TextField"
				:value-field="ValueField"
				:formatter="getMethodStatusText"
				:options="[
					{ text: i18n.t('status.notImplemented'), value: NotImplemented },
					{ text: i18n.t('status.partiallyImplemented'), value: PartiallyImplemented },
					{ text: i18n.t('status.conditionallyImplemented'), value: ConditionallyImplemented },
					{ text: i18n.t('status.implemented'), value: Implemented },
					{ text: i18n.t('status.willNotImplement'), value: WillNotImplement },
					{ text: i18n.t('status.willNotApply'), value: WillNotApply },
					{ text: i18n.t('status.needsAttention'), value: NeedsAttention },
				]"
			></MultiSelectFilter>
		</div>
		<div class="p-2 filter-col" v-if="columns.jira.value.isVisible">
			<SingleSelectFilter
				id="jira-filter-methods"
				v-model="columns.jira.value"
				:label-field="TextField"
				:value-field="ValueField"
				:options="[selectItem('misc.yes', true), selectItem('misc.no', false)]"
			></SingleSelectFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.audit.value.isVisible">
			<MultiSelectFilter
				id="audit-filter-methods"
				v-model="columns.audit.value"
				:label-field="TextField"
				:value-field="ValueField"
				:formatter="getAuditText"
				:options="[
					{ text: i18n.t('audit.active'), value: true },
					{ text: i18n.t('audit.finishedAudits'), value: false },
				]"
			></MultiSelectFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.auditName.value.isVisible">
			<TextFilter id="audit-name-filter-methods" v-model="columns.auditName.value"></TextFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.auditCheck.value.isVisible">
			<MultiSelectFilter
				id="audit-check-filter-methods"
				v-model="columns.auditCheck.value"
				:label-field="TextField"
				:value-field="ValueField"
				:options="[
					selectItem(i18n.t('audit.ok'), Ok),
					selectItem(i18n.t('audit.remark'), Remark),
					selectItem(i18n.t('audit.defect'), Defect),
					selectItem(i18n.t('audit.noKind'), NoKind),
				]"
			></MultiSelectFilter>
		</div>

		<div class="p-2 filter-col" v-show="props.showInstance && columns.instance.value.isVisible">
			<InstanceFilter id="instance-filter-methods" v-model="columns.instance.value"></InstanceFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.isRenewed.value.isVisible">
			<SingleSelectFilter
				id="renewed-filter-methods"
				v-model="columns.isRenewed.value"
				:label-field="TextField"
				:value-field="ValueField"
				:options="[selectItem('misc.yes', true), selectItem('misc.no', false)]"
			></SingleSelectFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.dueDate.value.isVisible">
			<CalendarFilter
				id="updated-at-filter-methods"
				v-model="columns.dueDate.value"
				:label-field="TextField"
				:value-field="ValueField"
			></CalendarFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.updatedAt.value.isVisible">
			<CalendarFilter
				id="due-date-filter-methods"
				v-model="columns.updatedAt.value"
				:label-field="TextField"
				:value-field="ValueField"
			></CalendarFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.comment.value.isVisible">
			<TextFilter id="comment-filter-methods" v-model="columns.comment.value"></TextFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.ruleComment.value.isVisible">
			<TextFilter id="rule-comment-filter-methods" v-model="columns.ruleComment.value"></TextFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.special.value.isVisible">
			<MultiSelectFilter
				id="special-filter-methods"
				v-model="columns.special.value"
				:label-field="TextField"
				:value-field="ValueField"
				:options="[
					selectItem('method.withoutComments', SMF_NoComment),
					selectItem('method.withComments', SMF_Comment),
					selectItem('method.withoutRuleComments', SMF_NoRuleComment),
					selectItem('method.withRuleComments', SMF_RuleComment),
				]"
			></MultiSelectFilter>
		</div>
	</FilterContainer>
</template>
